import React from "react";

import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { IconButton, Stack } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DocumentSectionType } from "constants/documents";
import { DocumentSection } from "store/types";

import { useContentContext } from "./content-context";

const iconButtonSx = {
  color: "#071f35",
  padding: "6px",
  "&:hover": {
    backgroundColor: "rgba(7, 31, 53, 0.08)"
  },
  "& svg": {
    fill: "#071f35"
  }
};

interface AddSectionToolbarProps {
  sections: DocumentSection[];
}

export const AddSectionToolbar: React.FC<AddSectionToolbarProps> = ({ sections }) => {
  const { createSection, currentSectionId } = useContentContext();

  const handleAddSection = async (type: DocumentSectionType, isAiMeeting?: boolean) => {
    if (!currentSectionId) return;

    // Find the index of the current section
    const currentSectionIndex = sections.findIndex((s) => s.id === currentSectionId);
    if (currentSectionIndex === -1) return;

    // Create new section after the current one
    await createSection({
      position: currentSectionIndex + 1,
      type,
      isAiMeeting
    });
  };

  return (
    <Stack direction="row" gap="3px">
      <MaterialTooltip isShow title="Add Text Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.Text)}>
          <ArticleOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add Image Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.Image)}>
          <ImageOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add Question Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.Question)}>
          <HelpOutlineOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add File Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.AIQuestion)}>
          <AttachFileOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add KPI Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.KPI)}>
          <SpeedOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add Graph Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.Graph)}>
          <InsertChartOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add Table Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.Table)}>
          <TableChartOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add Cashflow Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.CashflowGraph)}>
          <AccountBalanceOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add Transcripted Meeting Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.Meeting, true)}>
          <RecordVoiceOverOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>

      <MaterialTooltip isShow title="Add New Meeting Section" variant="dark">
        <IconButton sx={iconButtonSx} onClick={() => handleAddSection(DocumentSectionType.Meeting, false)}>
          <EventOutlinedIcon fontSize="sm" />
        </IconButton>
      </MaterialTooltip>
    </Stack>
  );
};
